import React, { useState, useEffect, useCallback } from 'react';
import './SettingsPage.css';
import { useTheme } from '../contexts/ThemeContext';
import {
  getProfile,
  updateProfile,
  NOTIFICATION_CATEGORIES,
} from '../services/profileService';

function SettingsPage() {
  const { isDark, toggleTheme } = useTheme();

  const [settings, setSettings] = useState({
    emailUpdates: false,
    weekStart: 'Sunday',
    timezone: 'UTC',
    language: 'English',
    autoSave: true,
    soundEffects: true
  });

  // Notification switches, one per category. These are the only settings on
  // this page that persist: the rest are still local state with a Save button
  // that does nothing, which is a separate job. `nearby` starts off because
  // the feature behind it needs a permission the phone has to grant.
  const [notifPrefs, setNotifPrefs] = useState({
    notif_reminders: true,
    notif_routines: true,
    notif_nearby: false,
    notif_deals: true,
    notif_updates: true,
  });

  useEffect(() => {
    let alive = true;
    getProfile()
      .then((p) => {
        if (!alive || !p) return;
        setNotifPrefs({
          notif_reminders: p.notif_reminders !== false,
          notif_routines: p.notif_routines !== false,
          notif_nearby: p.notif_nearby === true,
          notif_deals: p.notif_deals !== false,
          notif_updates: p.notif_updates !== false,
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Optimistic, and reverted if the write fails. A switch that waits on a
  // round-trip before moving reads as a broken switch.
  const toggleNotif = useCallback((key) => {
    setNotifPrefs((prev) => {
      const next = !prev[key];
      updateProfile({ [key]: next }).catch(() => {
        setNotifPrefs((cur) => ({ ...cur, [key]: !next }));
      });
      return { ...prev, [key]: next };
    });
  }, []);

  const handleToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleSelect = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  return (
    <div className="settings-wrapper">
      <div className="settings-card">
        <h1>Settings</h1>

        <div className="settings-section">
          <h2>Notifications</h2>
          <p className="section-note">
            Each of these is a separate channel on your phone, so silencing one
            leaves the others alone.
          </p>
          {NOTIFICATION_CATEGORIES.map((cat) => (
            <div className="setting-item" key={cat.key}>
              <div className="setting-info">
                <span className="setting-label">{cat.label}</span>
                <span className="setting-description">{cat.description}</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={!!notifPrefs[cat.key]}
                  onChange={() => toggleNotif(cat.key)}
                  aria-label={cat.label}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          ))}
          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Email Updates</span>
              <span className="setting-description">Get weekly summaries via email</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.emailUpdates}
                onChange={() => handleToggle('emailUpdates')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h2>Appearance</h2>
          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Dark Mode</span>
              <span className="setting-description">Switch to dark theme</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={isDark}
                onChange={toggleTheme}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Sound Effects</span>
              <span className="setting-description">Play sounds for interactions</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.soundEffects}
                onChange={() => handleToggle('soundEffects')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h2>Regional Settings</h2>
          <div className="setting-item-select">
            <label className="setting-label">Week Starts On</label>
            <select
              value={settings.weekStart}
              onChange={(e) => handleSelect('weekStart', e.target.value)}
              className="setting-select"
            >
              <option value="Sunday">Sunday</option>
              <option value="Monday">Monday</option>
            </select>
          </div>
          <div className="setting-item-select">
            <label className="setting-label">Timezone</label>
            <select
              value={settings.timezone}
              onChange={(e) => handleSelect('timezone', e.target.value)}
              className="setting-select"
            >
              <option value="UTC">UTC</option>
              <option value="EST">EST (Eastern)</option>
              <option value="PST">PST (Pacific)</option>
              <option value="CST">CST (Central)</option>
            </select>
          </div>
          <div className="setting-item-select">
            <label className="setting-label">Language</label>
            <select
              value={settings.language}
              onChange={(e) => handleSelect('language', e.target.value)}
              className="setting-select"
            >
              <option value="English">English</option>
              <option value="Spanish">Spanish</option>
              <option value="French">French</option>
              <option value="German">German</option>
            </select>
          </div>
        </div>

        <div className="settings-section">
          <h2>Task Management</h2>
          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-label">Auto-Save</span>
              <span className="setting-description">Automatically save changes</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.autoSave}
                onChange={() => handleToggle('autoSave')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h2>Privacy & Security</h2>
          <div className="settings-actions">
            <button className="btn-secondary">Change Password</button>
            <button className="btn-secondary">Export Data</button>
            <button className="btn-danger">Clear All Data</button>
          </div>
        </div>

        <div className="settings-footer">
          <button className="btn-save">Save Changes</button>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
